import { FilterSchemas, screener, ThemeCompany } from '@api';
import { hash32 } from '@helpers';
import { SortingState } from '@tanstack/react-table';
import {
  FilterGroups,
  FilterState,
  PerformanceFilterGroupKeys,
  SharedFilterState,
} from '@terminal/hooks/use-filter-actions/useFilterActions';
import { getThemeCompanies } from '@terminal/services/overview-widget/overview-widget-service';
import { apiFetch } from '@terminal/utils/api-fetch/apiFetch';
import { create } from '@terminal/utils/store/createStore';
import { RANK_FILTERS_KEY } from '@terminal/views/screener/use-screener-store/filter-utils';
import { TFunction } from 'i18next';
import { createRankFilterOption, getRankFilter } from '../filter-utils';
import { createThemeRankColumnId } from '../table-utils';

type PerformanceStore = {
  initStore: ({
    themeId,
    t,
    entityTags,
  }: {
    themeId: string;
    t: TFunction;
    entityTags?: string[];
  }) => Promise<void>;
  resetStore: () => void;
  isStoreInitialized: boolean;
  companiesTheme: ThemeCompany[];
  showBenchmark?: boolean;
  showBreakdown?: boolean;
  breakdownDisabled?: boolean;
  displayedEntityTags?: string[];
  tableSorting: SortingState;
  hash32ThemeId: string;
  setShowBenchmark: (benchmark: boolean) => void;
  setShowBreakdown: (breakdown: boolean) => void;
  setBreakdownDisabled: (breakdownDisabled: boolean) => void;
  setDisplayedEntityTags: (displayedEntityTags: string[]) => void;
  clearAllActiveFilterOptions: (t: TFunction) => void;
  setTableSorting: (tableSorting: SortingState) => void;
  setRankFilter: ({ t, value }: { t: TFunction; value: number }) => void;
} & SharedFilterState<PerformanceFilterGroupKeys>;

export const usePerformanceStore = create<PerformanceStore>((set, get) => ({
  initStore: async ({ themeId, t }) => {
    const { setRankFilter } = get();

    const [resFilters, resThemeCompanies] = await Promise.all([
      apiFetch<FilterSchemas>(screener.getFilters.path('theme_overview')),
      getThemeCompanies({ themeId }),
    ]);

    if (!resFilters.error) {
      const rankScreenerFilter = {
        group: 'THEME_PERF_FILTERS' as PerformanceFilterGroupKeys,
        schemas: [getRankFilter(t)],
      };

      const { groups } = resFilters.data;
      const filters = [rankScreenerFilter, ...groups];

      const filterGroups = filters.reduce(
        (res, group) => {
          const groupSchemas = group.schemas;
          const filterState: FilterState = {
            allFilters: groupSchemas,
            activeFilterOptions: [],
            activeFilters: groupSchemas.filter(f => f.visible),
          };

          res[group.group as keyof typeof res] = filterState;
          return res;
        },
        {} as FilterGroups<PerformanceFilterGroupKeys>
      );

      filterGroups[rankScreenerFilter.group] = {
        allFilters: rankScreenerFilter.schemas,
        activeFilterOptions: [],
        activeFilters: rankScreenerFilter.schemas,
      };

      set({ filterGroups });
      setRankFilter({ t, value: 10 });
    }

    if (!resThemeCompanies.error) {
      set({ companiesTheme: resThemeCompanies.data ?? [] });
    }

    // We need to replicate hashing done one the BE to be able to use ranking provided by the API
    const hash32ThemeId = hash32(themeId);
    set({
      hash32ThemeId: hash32ThemeId,
      tableSorting: [
        {
          id: createThemeRankColumnId(hash32ThemeId),
          desc: false,
        },
      ],
      isStoreInitialized: true,
    });
  },
  resetStore: () => {
    set({
      showBenchmark: false,
      showBreakdown: false,
      breakdownDisabled: false,
      tableSorting: [],
      hash32ThemeId: '',
      isStoreInitialized: false,
    });
  },
  clearAllActiveFilterOptions: t => {
    const { setRankFilter } = get();
    set(state => {
      const clearedFilterGroups = Object.keys(state.filterGroups).reduce(
        (acc, groupKey) => {
          const key = groupKey as PerformanceFilterGroupKeys;
          acc[key] = {
            ...state.filterGroups[key],
            activeFilterOptions: [],
          };
          return acc;
        },
        {} as FilterGroups<PerformanceFilterGroupKeys>
      );

      return { filterGroups: clearedFilterGroups };
    });
    setRankFilter({ t, value: 10 });
  },
  hash32ThemeId: '',
  filterGroups: {} as FilterGroups<PerformanceFilterGroupKeys>,
  companiesTheme: [],
  showBenchmark: false,
  showBreakdown: false,
  breakdownDisabled: false,
  isStoreInitialized: false,
  displayedEntityTags: [],
  tableSorting: [],
  setShowBenchmark: showBenchmark => set({ showBenchmark }),
  setShowBreakdown: showBreakdown => set({ showBreakdown }),
  setBreakdownDisabled: breakdownDisabled => set({ breakdownDisabled }),
  setDisplayedEntityTags: displayedEntityTags => set({ displayedEntityTags }),
  setTableSorting: tableSorting => set({ tableSorting }),
  setRankFilter: ({ t, value }) => {
    const { filterGroups } = get();
    filterGroups.THEME_PERF_FILTERS.activeFilterOptions = [
      {
        id: RANK_FILTERS_KEY,
        filter: RANK_FILTERS_KEY,
        options: [createRankFilterOption({ t, value })],
      },
    ];
    set({ filterGroups });
  },
}));
